import React, { useRef } from 'react';
import { classNames } from 'primereact/utils';
import { Route, Routes, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Divider } from 'components/_new/Divider';
import { usePanel } from 'contexts/panelContext';
import { useAuth } from 'contexts/authContext';
import { Breadcrumbs } from './Breadcrumbs';
import { useKnowledgeBase } from './knowledgeBaseContext';
import { KnowledgeBaseArticle } from './KnowledgeBaseArticle';
import { DirectoryResults, FlagResults, Results, TagResults } from './Results';
import { SearchBar } from './SearchBar';
import { RecommendedArticles } from './RecommendedArticles';
import { FlaggedArticles } from './FlaggedArticles';
import { ProgramArticles } from './ProgramArticles';
import { DirectoryCards } from './DirectoryCards';
import { TaggedArticles } from './TaggedArticles';
import { BlogArticles } from './BlogArticles';

export const KnowledgeBase = () => {
	const { t } = useTranslation();
	const { isArticlePage, isMainPage, isSearch, isDirectory } = useKnowledgeBase();
	const kbContainerRef = useRef<HTMLDivElement>(null);

	const { currentUser } = useAuth();
	const { organizationName, programName, type: panelType } = useParams();
	const { panelLinkPrefix, currentProgram } = usePanel();
	const contentParserOptions = {
		replaces: {
			panelPrefix: panelLinkPrefix,
			organization: organizationName,
			program: programName,
			usertype: panelType,
		},
		disableFormat: true,
	};

	return (
		<div
			className={classNames('flex flex-column gap-2', {
				'pb-3': !isArticlePage,
				'h-full overflow-y-hidden': isArticlePage || isDirectory,
			})}
		>
			<Breadcrumbs />
			<div
				id="knowledgeBaseContainer"
				ref={kbContainerRef}
				className={classNames('flex flex-column gap-4 ', {
					'flex-1 h-full overflow-y-scroll': isArticlePage || isDirectory,
				})}
			>
				<Routes>
					<Route
						index
						element={
							<>
								<SearchBar />
								{isMainPage && !isSearch && (
									<>
										<RecommendedArticles
											renderWrapper={(hasArticles, children) =>
												hasArticles ? (
													<>
														<div className="flex flex-column gap-1">
															<h3 className="text-base">
																{t('knowledgeBase.homepage.recommendedArticles')}
															</h3>
															{children}
														</div>
														<Divider />
													</>
												) : null
											}
											contentParserOptions={contentParserOptions}
										/>

										<ProgramArticles
											renderWrapper={(hasArticles, children) =>
												hasArticles ? (
													<>
														<div className="flex flex-column gap-1">{children}</div>
														<Divider />
													</>
												) : null
											}
											contentParserOptions={contentParserOptions}
										/>

										{currentProgram?.professionalKnowledgeAccess &&
											currentUser?.isProfessionalViewEnabled && (
												<div className="flex flex-column gap-1">
													<h3 className="text-base">
														{t('knowledgeBase.homepage.techniquesByApplication')}
													</h3>
													<TaggedArticles />
												</div>
											)}

										<Divider />

										<div className="flex flex-column gap-1">
											<h3 className="text-base">
												{t('knowledgeBase.homepage.allMaterialsByCategory')}
											</h3>
											<DirectoryCards />
										</div>

										<Divider />

										<div className="flex flex-column gap-1">
											<h3 className="text-base">{t('knowledgeBase.homepage.markedMaterials')}</h3>
											<FlaggedArticles />
										</div>
										<Divider />

										<div className="flex flex-column gap-1">
											<h3 className="text-base">{t('knowledgeBase.homepage.fromBlog')}</h3>
											<BlogArticles />
										</div>
									</>
								)}
								<Results />
							</>
						}
					/>
					<Route path="favorites" element={<FlagResults flag="favorite" />} />
					<Route path="toread" element={<FlagResults flag="toread" />} />
					<Route path="readed" element={<FlagResults flag="readed" />} />
					<Route
						path="directory/*"
						element={
							<>
								<SearchBar />
								<DirectoryResults />
							</>
						}
					/>
					<Route
						path="tag/*"
						element={
							<>
								<SearchBar />
								<TagResults />
							</>
						}
					/>
					<Route
						path="article/*"
						element={
							<KnowledgeBaseArticle scrollContainerRef={isArticlePage ? kbContainerRef : undefined} />
						}
					/>
				</Routes>

				{/* {isMainPage && !isSearch && <TopCards />} */}

				{/* {isMainPage && !isSearch && <FeaturedArticles />}
				{isMainPage && !isSearch && <TagCloud />} */}
			</div>
		</div>
	);
};
